import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

export const DEFAULT_SPIRAL_COLORS = [
  '#FA8904',
  '#16a34a',
  '#f59e0b',
  '#ef4444',
  '#a855f7',
  '#ec4899',
];

export function emptyProductQrStyle() {
  return {
    color_mode: 'black_gray',
    primary_color: '#FA8904',
    palette_mode: 'spiral',
    palette_colors: [...DEFAULT_SPIRAL_COLORS],
    module_shape: 'square',
    qr_shape: 'square',
    watermark_enabled: true,
    watermark_preset: 'org_name',
    watermark_text: '',
    payload_format: 'standard',
    center_logo_base64: '',
  };
}

export function productQrStyleFromApi(apiStyle) {
  if (!apiStyle) return emptyProductQrStyle();
  return {
    color_mode: apiStyle.color_mode || 'black_gray',
    primary_color: apiStyle.primary_color || '#FA8904',
    palette_mode: apiStyle.palette_mode || 'spiral',
    palette_colors:
      apiStyle.palette_colors?.length >= 2
        ? [...apiStyle.palette_colors]
        : [...DEFAULT_SPIRAL_COLORS],
    module_shape: apiStyle.module_shape || 'square',
    qr_shape: apiStyle.qr_shape || 'square',
    watermark_enabled: apiStyle.watermark_enabled !== false,
    watermark_preset: apiStyle.watermark_preset || 'org_name',
    watermark_text: apiStyle.watermark_text || '',
    payload_format: apiStyle.payload_format || 'standard',
    center_logo_base64: apiStyle.center_logo_base64 || '',
  };
}

export function buildQrStylePayload(qrStyle) {
  const colorMode = qrStyle.color_mode || 'black_gray';
  const payload = {
    color_mode: colorMode,
    module_shape: qrStyle.module_shape || 'square',
    qr_shape: qrStyle.qr_shape || 'square',
    watermark_enabled: qrStyle.watermark_enabled !== false,
    watermark_preset: qrStyle.watermark_preset || 'org_name',
    payload_format: qrStyle.payload_format || 'standard',
  };

  if (qrStyle.watermark_preset === 'custom' && qrStyle.watermark_text?.trim()) {
    payload.watermark_text = qrStyle.watermark_text.trim();
  }

  // Always send logo field so clearing it persists on update (null removes).
  payload.center_logo_base64 = qrStyle.center_logo_base64?.trim() || null;

  if (colorMode === 'color') {
    payload.primary_color = qrStyle.primary_color || '#FA8904';
  }

  if (colorMode === 'multi_color') {
    const colors = (qrStyle.palette_colors || []).map((c) => c.trim()).filter(Boolean);
    if (colors.length < 2) return null;
    payload.palette_mode = qrStyle.palette_mode || 'spiral';
    payload.palette_colors = colors;
  }

  return payload;
}

export default function ProductQrStyleFields({ value, onChange, isDark }) {
  const { t } = useTranslation();
  const logoInputRef = useRef(null);
  const colors = value.palette_colors || DEFAULT_SPIRAL_COLORS;
  const labelCls = isDark ? 'text-zinc-300' : 'text-zinc-700';
  const inputCls = isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900';
  const selectCls = `h-9 ${inputCls}`;
  const fieldCls = 'space-y-1';

  const patch = (partial) => onChange({ ...value, ...partial });

  const clearLogo = () => {
    patch({ center_logo_base64: '' });
    if (logoInputRef.current) logoInputRef.current.value = '';
  };
  const setColors = (next) => patch({ palette_colors: next });

  const showMultiColor = value.color_mode === 'multi_color';
  const showPrimaryColor = value.color_mode === 'color';

  return (
    <div
      className={`rounded-lg border p-4 space-y-4 ${isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-slate-200 bg-slate-50'}`}
    >
      <div>
        <p className={`text-sm font-medium ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
          {t('products.qrStyleTitle')}
        </p>
        <p className={`text-xs mt-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('products.qrStyleHint')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className={fieldCls}>
          <Label className={labelCls}>{t('generateQr.colorMode')}</Label>
          <Select value={value.color_mode} onValueChange={(v) => patch({ color_mode: v })}>
            <SelectTrigger className={selectCls}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200'}>
              <SelectItem value="grayscale">{t('generateQr.colorGrayscale')}</SelectItem>
              <SelectItem value="black_white">{t('generateQr.colorBlackWhite')}</SelectItem>
              <SelectItem value="black_gray">{t('generateQr.colorBlackGray')}</SelectItem>
              <SelectItem value="color">{t('generateQr.colorColor')}</SelectItem>
              <SelectItem value="multi_color">{t('generateQr.colorMulti')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className={fieldCls}>
          <Label className={labelCls}>{t('generateQr.moduleStyle')}</Label>
          <Select value={value.module_shape} onValueChange={(v) => patch({ module_shape: v })}>
            <SelectTrigger className={selectCls}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200'}>
              <SelectItem value="square">{t('generateQr.moduleSquare')}</SelectItem>
              <SelectItem value="round">{t('generateQr.moduleRound')}</SelectItem>
              <SelectItem value="square_round">{t('generateQr.moduleSquareRound')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className={fieldCls}>
          <Label className={labelCls}>{t('generateQr.qrShape')}</Label>
          <Select value={value.qr_shape} onValueChange={(v) => patch({ qr_shape: v })}>
            <SelectTrigger className={selectCls}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200'}>
              <SelectItem value="square">{t('generateQr.shapeSquare')}</SelectItem>
              <SelectItem value="circle">{t('generateQr.shapeCircle')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className={fieldCls}>
          <Label className={labelCls}>{t('generateQr.payloadFormat')}</Label>
          <Select value={value.payload_format} onValueChange={(v) => patch({ payload_format: v })}>
            <SelectTrigger className={selectCls}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200'}>
              <SelectItem value="standard">{t('generateQr.payloadStandard')}</SelectItem>
              <SelectItem value="gs1_digital_link">{t('generateQr.payloadGs1')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {showPrimaryColor ? (
        <div className={fieldCls}>
          <Label className={labelCls}>{t('generateQr.primaryColor')}</Label>
          <Input
            value={value.primary_color}
            onChange={(e) => patch({ primary_color: e.target.value })}
            placeholder="#FA8904"
            className={inputCls}
          />
        </div>
      ) : null}

      {showMultiColor ? (
        <div className="space-y-3">
          <div className={fieldCls}>
            <Label className={labelCls}>{t('generateQr.multiColorStyle')}</Label>
            <Select value={value.palette_mode} onValueChange={(v) => patch({ palette_mode: v })}>
              <SelectTrigger className={selectCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200'}>
                <SelectItem value="spiral">{t('products.paletteSpiral')}</SelectItem>
                <SelectItem value="quadrant">{t('generateQr.paletteQuadrant')}</SelectItem>
                <SelectItem value="rows">{t('generateQr.paletteRows')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className={labelCls}>{t('products.spiralColors')}</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {colors.map((c, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="color"
                    value={c.startsWith('#') ? c : `#${c.replace('#', '')}`}
                    onChange={(e) => {
                      const next = [...colors];
                      next[idx] = e.target.value;
                      setColors(next);
                    }}
                    className="h-9 w-10 rounded border cursor-pointer shrink-0"
                    aria-label={`Color ${idx + 1}`}
                  />
                  <Input
                    value={c}
                    onChange={(e) => {
                      const next = [...colors];
                      next[idx] = e.target.value;
                      setColors(next);
                    }}
                    className={`${inputCls} font-mono text-xs`}
                  />
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {colors.length < 12 ? (
                <Button type="button" variant="outline" size="sm" onClick={() => setColors([...colors, '#6366f1'])}>
                  {t('products.addSpiralColor')}
                </Button>
              ) : null}
              {colors.length > 2 ? (
                <Button type="button" variant="outline" size="sm" onClick={() => setColors(colors.slice(0, -1))}>
                  {t('products.removeSpiralColor')}
                </Button>
              ) : null}
              <Button type="button" variant="ghost" size="sm" onClick={() => setColors([...DEFAULT_SPIRAL_COLORS])}>
                {t('products.resetSpiralColors')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <div className={fieldCls}>
        <Label className={labelCls}>{t('generateQr.companyLogo')}</Label>
        <Input
          ref={logoInputRef}
          type="file"
          accept="image/*"
          className={`${inputCls} text-xs file:mr-2 file:rounded file:border-0 file:bg-primary file:px-2 file:py-1 file:text-primary-foreground`}
          onChange={(e) => {
            const f = e.target.files && e.target.files[0];
            if (!f) {
              clearLogo();
              return;
            }
            const reader = new FileReader();
            reader.onload = () => patch({ center_logo_base64: String(reader.result || '') });
            reader.readAsDataURL(f);
          }}
        />
        {value.center_logo_base64 ? (
          <div className="flex items-center gap-2 mt-1">
            <img src={value.center_logo_base64} alt="" className="w-8 h-8 rounded border bg-white object-contain" />
            <Button type="button" variant="outline" size="sm" onClick={clearLogo}>
              {t('generateQr.removeLogo')}
            </Button>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className={fieldCls}>
          <Label className={labelCls}>{t('generateQr.watermark')}</Label>
          <label className="flex items-center gap-2 h-9">
            <input
              type="checkbox"
              checked={value.watermark_enabled}
              onChange={(e) => patch({ watermark_enabled: e.target.checked })}
            />
            <span className={`text-sm ${isDark ? 'text-zinc-200' : 'text-slate-700'}`}>
              {t('generateQr.watermarkEnable')}
            </span>
          </label>
        </div>

        {value.watermark_enabled ? (
          <>
            <div className={fieldCls}>
              <Label className={labelCls}>{t('generateQr.watermarkText')}</Label>
              <Select value={value.watermark_preset} onValueChange={(v) => patch({ watermark_preset: v })}>
                <SelectTrigger className={selectCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200'}>
                  <SelectItem value="org_name">{t('generateQr.watermarkOrg')}</SelectItem>
                  <SelectItem value="original_printed">{t('generateQr.watermarkOriginal')}</SelectItem>
                  <SelectItem value="custom">{t('generateQr.watermarkCustom')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {value.watermark_preset === 'custom' ? (
              <div className={`${fieldCls} sm:col-span-2`}>
                <Label className={labelCls}>{t('generateQr.watermarkCustom')}</Label>
                <Input
                  value={value.watermark_text}
                  onChange={(e) => patch({ watermark_text: e.target.value })}
                  placeholder={t('generateQr.watermarkCustomPlaceholder')}
                  className={inputCls}
                  maxLength={32}
                />
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
